<template>
  <div class="business-hours">
    <Icon 
      :icon="getBusinessIcon" 
      class="business-hours__icon" 
      :class="getBusinessIconClass"
    />
    <div class="business-hours__content">
      <span class="business-hours__title" :class="getBusinessTitleClass">
        {{ businessStatus.message }}
      </span>
      <span class="business-hours__time">{{ businessHours }}</span>
      <span class="business-hours__subtitle">{{ businessStatus.subtitle }}</span>
      
      <!-- Guidance section for when closed or closing soon -->
      <div v-if="showGuidance" class="business-hours__guidance">
        <p class="business-hours__guidance-text">{{ businessStatus.guidance }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import { Icon } from '@iconify/vue';
import { useBusinessHours } from '@/composables/chat/useBusinessHours';

export default {
  name: 'BusinessHours',
  components: {
    Icon
  },
  props: {
    businessHours: {
      type: String,
      default: 'Mon-Fri 9AM-5PM EST'
    },
    showGuidance: {
      type: Boolean,
      default: true
    }
  },
  setup() {
    const { isBusinessOpen, isClosingSoon, isOpeningSoon, businessStatus } = useBusinessHours();

    const getBusinessIcon = computed(() => {
      if (isBusinessOpen.value) return 'mdi:clock-check';
      if (isOpeningSoon.value) return 'mdi:clock-fast';
      return 'mdi:clock-outline';
    });

    const getBusinessIconClass = computed(() => ({
      'business-hours__icon--open': isBusinessOpen.value && !isClosingSoon.value,
      'business-hours__icon--closing': isClosingSoon.value,
      'business-hours__icon--opening': isOpeningSoon.value,
      'business-hours__icon--closed': !isBusinessOpen.value && !isOpeningSoon.value
    }));

    const getBusinessTitleClass = computed(() => ({
      'business-hours__title--open': isBusinessOpen.value && !isClosingSoon.value,
      'business-hours__title--closing': isClosingSoon.value,
      'business-hours__title--opening': isOpeningSoon.value
    }));

    const showGuidanceComputed = computed(() => {
      return (!isBusinessOpen.value || isClosingSoon.value);
    });

    return {
      businessStatus,
      getBusinessIcon,
      getBusinessIconClass,
      getBusinessTitleClass,
      showGuidance: showGuidanceComputed
    };
  }
};
</script>

<style scoped>
.business-hours {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.business-hours__icon {
  width: 16px;
  height: 16px;
  color: #6B7280;
  flex-shrink: 0;
  margin-top: 2px;
}

.business-hours__icon--open {
  color: #10B981;
}

.business-hours__icon--closing {
  color: #F59E0B;
}

.business-hours__icon--opening {
  color: #3B82F6;
}

.business-hours__icon--closed {
  color: #EF4444;
}

.business-hours__content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.business-hours__title {
  font-size: 12px;
  font-weight: 600;
  color: #111827;
}

.business-hours__title--open {
  color: #10B981;
}

.business-hours__title--closing {
  color: #F59E0B;
}

.business-hours__title--opening {
  color: #3B82F6;
}

.business-hours__time {
  font-size: 12px;
  color: #6B7280;
}

.business-hours__subtitle {
  font-size: 11px;
  color: #9CA3AF;
  font-style: italic;
}

.business-hours__guidance {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #E5E7EB;
}

.business-hours__guidance-text {
  font-size: 11px;
  line-height: 1.4;
  color: #6B7280;
  margin: 0;
}

/* Mobile styles */
@media (max-width: 768px) {
  .business-hours__icon {
    width: 20px;
    height: 20px;
  }

  .business-hours__title {
    font-size: 14px;
  }

  .business-hours__time {
    font-size: 13px;
  }

  .business-hours__subtitle {
    font-size: 12px;
  }

  .business-hours__guidance {
    margin-top: 12px;
    padding-top: 12px;
  }

  .business-hours__guidance-text {
    font-size: 12px;
  }
}
</style> 